/* Lounelle — mobile natural-ratio Flickity track v6
 * Keeps the selected product image fully visible on mobile while preserving
 * Palo Alto's native desktop gallery geometry. Mobile overrides are reversible.
 * Gallery geometry is paused while the product PhotoSwipe viewer is open.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 767px)');
  var ROOT_SELECTOR = 'product-info';
  var SLIDER_SELECTOR = '[data-product-single-media-slider]';
  var SLIDE_SELECTOR = '[data-product-single-media-wrapper]';
  var rafId = 0;

  function isProductZoomOpen() {
    return !!document.querySelector('.pswp.pswp--lounelle-mobile.pswp--open');
  }

  function getFlickity(slider) {
    try {
      return window.theme && window.theme.Flickity && window.theme.Flickity.data
        ? window.theme.Flickity.data(slider)
        : null;
    } catch (_) {
      return null;
    }
  }

  function mediaRatio(slide) {
    var ratio = parseFloat(slide.dataset.aspectratio || '0');
    if (ratio > 0) return ratio;

    var img = slide.querySelector('img');
    if (img) {
      var width = parseFloat(img.getAttribute('width') || img.naturalWidth || '0');
      var height = parseFloat(img.getAttribute('height') || img.naturalHeight || '0');
      if (width > 0 && height > 0) return width / height;
    }

    return 1;
  }

  function retry(root, attempt) {
    if ((attempt || 0) >= 12 || !MOBILE.matches || isProductZoomOpen()) return;
    window.setTimeout(function () {
      applyGeometry(root, (attempt || 0) + 1);
    }, 60);
  }

  function rememberNativeFlickity(root, flkty) {
    if (!root || !flkty || root._lounelleNativeFlickity) return;

    root._lounelleNativeFlickity = {
      cellAlign: flkty.options ? flkty.options.cellAlign : undefined,
      percentPosition: flkty.options ? flkty.options.percentPosition : undefined,
      wrapAround: flkty.options ? flkty.options.wrapAround : undefined,
      numericCellAlign: typeof flkty.cellAlign === 'number' ? flkty.cellAlign : undefined
    };
  }

  function refreshFlickity(flkty, selected) {
    if (!flkty) return;

    if (typeof flkty.resize === 'function') flkty.resize();
    if (typeof flkty.getSize === 'function') flkty.getSize();
    if (typeof flkty.positionCells === 'function') flkty.positionCells();
    if (typeof flkty._getWrapShiftCells === 'function') flkty._getWrapShiftCells();
    if (typeof flkty.setGallerySize === 'function') flkty.setGallerySize();
    if (typeof flkty.reposition === 'function') flkty.reposition();

    var current = typeof selected === 'number'
      ? selected
      : (typeof flkty.selectedIndex === 'number' ? flkty.selectedIndex : 0);
    var maxIndex = flkty.cells && flkty.cells.length ? flkty.cells.length - 1 : current;
    var safeIndex = Math.max(0, Math.min(current, maxIndex));

    if (typeof flkty.select === 'function') {
      flkty.select(safeIndex, false, true);
    } else if (typeof flkty.positionSliderAtSelected === 'function') {
      flkty.positionSliderAtSelected();
    }
  }

  function applyGeometry(root, attempt) {
    if (!MOBILE.matches || isProductZoomOpen() || !root || !document.documentElement.contains(root)) return;

    var slider = root.querySelector(SLIDER_SELECTOR);
    if (!slider) return;

    var slides = Array.prototype.slice.call(slider.querySelectorAll(SLIDE_SELECTOR));
    if (!slides.length) return;

    var flkty = getFlickity(slider);
    if (slides.length > 1 && !flkty) {
      retry(root, attempt);
      return;
    }

    var viewport = slider.querySelector('.flickity-viewport');
    if (!viewport) {
      retry(root, attempt);
      return;
    }

    var viewportHeight = viewport.getBoundingClientRect().height;
    var trackWidth = slider.getBoundingClientRect().width;
    if (!viewportHeight || !trackWidth) {
      retry(root, attempt);
      return;
    }

    slides.forEach(function (slide) {
      var ratio = mediaRatio(slide);
      var naturalWidthAtTrackHeight = Math.round(viewportHeight * ratio);
      var cellWidth = Math.max(1, Math.min(trackWidth, naturalWidthAtTrackHeight));
      var widthValue = cellWidth + 'px';

      slide.style.setProperty('--lounelle-mobile-cell-width', widthValue);
      slide.style.setProperty('width', widthValue, 'important');
      slide.style.setProperty('min-width', widthValue, 'important');
      slide.style.setProperty('max-width', widthValue, 'important');
      slide.style.setProperty('margin-left', '0px', 'important');
      slide.style.setProperty('margin-right', '0px', 'important');
    });

    if (!flkty) return;

    rememberNativeFlickity(root, flkty);
    var selected = typeof flkty.selectedIndex === 'number' ? flkty.selectedIndex : 0;

    if (flkty.options) {
      flkty.options.cellAlign = 'center';
      flkty.options.percentPosition = false;
      flkty.options.wrapAround = true;
    }

    if (typeof flkty.cellAlign === 'number') flkty.cellAlign = 0.5;
    refreshFlickity(flkty, selected);

    if (typeof flkty.positionSliderAtSelected === 'function') {
      flkty.positionSliderAtSelected();
    }
  }

  function clearMobileSlideStyles(slider) {
    if (!slider) return;

    Array.prototype.forEach.call(slider.querySelectorAll(SLIDE_SELECTOR), function (slide) {
      slide.style.removeProperty('--lounelle-mobile-cell-width');
      slide.style.removeProperty('width');
      slide.style.removeProperty('min-width');
      slide.style.removeProperty('max-width');
      slide.style.removeProperty('margin-left');
      slide.style.removeProperty('margin-right');
    });
  }

  function resetGeometry(root) {
    if (!root || !document.documentElement.contains(root)) return;

    if (root._lounelleTrackTimer) {
      window.clearTimeout(root._lounelleTrackTimer);
      root._lounelleTrackTimer = null;
    }

    var slider = root.querySelector(SLIDER_SELECTOR);
    if (!slider) return;

    clearMobileSlideStyles(slider);

    var flkty = getFlickity(slider);
    if (!flkty) return;

    var selected = typeof flkty.selectedIndex === 'number' ? flkty.selectedIndex : 0;
    var nativeOptions = root._lounelleNativeFlickity;

    if (nativeOptions) {
      if (flkty.options) {
        if (nativeOptions.cellAlign !== undefined) flkty.options.cellAlign = nativeOptions.cellAlign;
        if (nativeOptions.percentPosition !== undefined) flkty.options.percentPosition = nativeOptions.percentPosition;
        if (nativeOptions.wrapAround !== undefined) flkty.options.wrapAround = nativeOptions.wrapAround;
      }
      if (nativeOptions.numericCellAlign !== undefined) {
        flkty.cellAlign = nativeOptions.numericCellAlign;
      }
    }

    window.requestAnimationFrame(function () {
      refreshFlickity(flkty, selected);
      window.requestAnimationFrame(function () {
        refreshFlickity(flkty, selected);
      });
    });
  }

  function schedule(root) {
    if (!MOBILE.matches || isProductZoomOpen() || !root) return;

    window.cancelAnimationFrame(rafId);
    rafId = window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (isProductZoomOpen()) return;

        applyGeometry(root, 0);

        if (root._lounelleTrackTimer) window.clearTimeout(root._lounelleTrackTimer);
        root._lounelleTrackTimer = window.setTimeout(function () {
          applyGeometry(root, 0);
        }, 140);
      });
    });
  }

  function init(root) {
    if (!MOBILE.matches || !root) return;

    schedule(root);

    if (root.dataset.lounelleNaturalTrack === 'true') return;
    root.dataset.lounelleNaturalTrack = 'true';

    var slider = root.querySelector(SLIDER_SELECTOR);
    if (!slider) return;

    var observer = new MutationObserver(function (mutations) {
      if (!MOBILE.matches || isProductZoomOpen()) return;

      var mediaChanged = mutations.some(function (mutation) {
        var nodes = Array.prototype.slice.call(mutation.addedNodes)
          .concat(Array.prototype.slice.call(mutation.removedNodes));

        return nodes.some(function (node) {
          return node.nodeType === 1 &&
            ((node.matches && node.matches(SLIDE_SELECTOR)) ||
             (node.querySelector && node.querySelector(SLIDE_SELECTOR)));
        });
      });

      if (mediaChanged) schedule(root);
    });

    observer.observe(slider, { childList: true });
  }

  function roots(scope) {
    var container = scope || document;
    var found = [];

    if (container.matches && container.matches(ROOT_SELECTOR)) found.push(container);
    Array.prototype.forEach.call(container.querySelectorAll(ROOT_SELECTOR), function (root) {
      if (found.indexOf(root) === -1) found.push(root);
    });

    return found;
  }

  function initAll(scope) {
    if (!document.body || !document.body.classList.contains('template-product')) return;
    roots(scope).forEach(init);
  }

  function resetAll(scope) {
    if (!document.body || !document.body.classList.contains('template-product')) return;
    roots(scope).forEach(resetGeometry);
  }

  function refreshAll() {
    if (isProductZoomOpen()) return;
    if (MOBILE.matches) initAll(document);
    else resetAll(document);
  }

  function boot() {
    refreshAll();

    document.addEventListener('theme:product-info:loaded', function (event) {
      var root = event.target.closest(ROOT_SELECTOR) || event.target;
      if (MOBILE.matches) schedule(root);
      else resetGeometry(root);
    });

    document.addEventListener('shopify:section:load', function (event) {
      if (MOBILE.matches) initAll(event.target);
      else resetAll(event.target);
    });

    window.addEventListener('resize', refreshAll, { passive: true });
    window.addEventListener('orientationchange', refreshAll, { passive: true });

    if (MOBILE.addEventListener) {
      MOBILE.addEventListener('change', refreshAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
